/**
 * Superadmin login (ADR-0036 Phase 2 + F2.0 2FA) — a SEPARATE realm from the operator
 * login. Mints a `realm='platform'` token stored in its own `usePlatformAuth` store.
 *
 * Polymorphic by the login `status`: `ok` opens a session; `totp_required` shows a
 * code step; `totp_setup_required` (enforcement) drives forced enrollment via the
 * shared `TotpEnroll` over the platform setup adapter.
 */
import { ShieldAlert } from "lucide-react";
import * as React from "react";
import { useNavigate } from "react-router-dom";

import { TotpEnroll, type TotpBegin } from "@/components/auth/totp";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { type PlatformAdmin, PlatformApiError, platformApi, usePlatformAuth } from "@/lib/platform";
import { platformSetupAdapter } from "@/lib/platformTotp";

type Stage =
  | { kind: "password" }
  | { kind: "totp"; challenge: string }
  | { kind: "setup"; setupToken: string; begin: TotpBegin };

export function PlatformLoginPage() {
  const navigate = useNavigate();
  const setSession = usePlatformAuth((s) => s.setSession);
  const [email, setEmail] = React.useState("");
  const [password, setPassword] = React.useState("");
  const [code, setCode] = React.useState("");
  const [stage, setStage] = React.useState<Stage>({ kind: "password" });
  const [error, setError] = React.useState<string | null>(null);
  const [busy, setBusy] = React.useState(false);

  function open(token: string, admin: PlatformAdmin, refreshToken?: string | null) {
    setSession(token, admin, refreshToken);
    navigate("/platform");
  }

  /** Adopt a session token that has no admin payload (TOTP/setup paths): set it, then
   *  resolve `/auth/me` with it, then store the real admin. */
  async function openWithToken(token: string) {
    setSession(token, { id: "", email: "", full_name: null });
    open(token, await platformApi.me());
  }

  async function submitPassword(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const res = await platformApi.login(email.trim().toLowerCase(), password);
      if (res.status === "ok" && res.token && res.admin) {
        open(res.token, res.admin, res.refresh_token);
      } else if (res.status === "totp_required" && res.challenge_token) {
        setStage({ kind: "totp", challenge: res.challenge_token });
      } else if (res.status === "totp_setup_required" && res.setup_token) {
        const begin = await platformSetupAdapter(res.setup_token).begin();
        setStage({ kind: "setup", setupToken: res.setup_token, begin });
      }
    } catch (err) {
      setError(err instanceof PlatformApiError ? "Email o password non validi" : "Errore di rete");
    } finally {
      setBusy(false);
    }
  }

  async function submitTotp(e: React.FormEvent) {
    e.preventDefault();
    if (stage.kind !== "totp") return;
    setBusy(true);
    setError(null);
    try {
      const res = await platformApi.loginTotp(stage.challenge, code.trim());
      if (res.status === "ok" && res.token && res.admin)
        open(res.token, res.admin, res.refresh_token);
    } catch {
      setError("Codice non valido");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-background p-4">
      <div className="w-full max-w-sm space-y-5 rounded-xl border border-border/60 bg-surface p-8 shadow-lg">
        <div className="flex items-center gap-2 text-foreground">
          <ShieldAlert className="h-5 w-5 text-danger" strokeWidth={1.5} />
          <h1 className="text-lg font-semibold">Superadmin</h1>
        </div>

        {stage.kind === "password" && (
          <form onSubmit={submitPassword} className="space-y-5">
            <p className="text-sm text-muted-foreground">
              Control plane della piattaforma. Realm separato dagli operatori.
            </p>
            <div className="space-y-1.5">
              <Label htmlFor="su-email">Email</Label>
              <Input
                id="su-email"
                type="email"
                autoFocus
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="su-pw">Password</Label>
              <Input
                id="su-pw"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
            </div>
            {error && <p className="text-sm text-danger">{error}</p>}
            <Button type="submit" className="w-full" disabled={busy || !email || !password}>
              {busy ? "Accesso…" : "Accedi"}
            </Button>
          </form>
        )}

        {stage.kind === "totp" && (
          <form onSubmit={submitTotp} className="space-y-5">
            <p className="text-sm text-muted-foreground">
              Inserisci il codice del tuo autenticatore (o un codice di recupero).
            </p>
            <div className="space-y-1.5">
              <Label htmlFor="su-code">Codice</Label>
              <Input
                id="su-code"
                autoFocus
                inputMode="numeric"
                autoComplete="one-time-code"
                value={code}
                onChange={(e) => setCode(e.target.value)}
                placeholder="123456"
              />
            </div>
            {error && <p className="text-sm text-danger">{error}</p>}
            <Button type="submit" className="w-full" disabled={busy || !code}>
              {busy ? "Verifica…" : "Verifica"}
            </Button>
          </form>
        )}

        {stage.kind === "setup" && (
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground">
              La 2FA è obbligatoria per i superadmin. Configurala per continuare.
            </p>
            <TotpEnroll
              begin={stage.begin}
              onConfirm={(c) => platformSetupAdapter(stage.setupToken).confirm(c)}
              onDone={(result) => {
                if (result.access_token) void openWithToken(result.access_token);
              }}
            />
          </div>
        )}
      </div>
    </div>
  );
}
